import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-fc-calc-section',
    standalone: true,
    imports: [ReactiveFormsModule, TranslateModule, CommonModule],
    templateUrl: './fc-calc-section.component.html',
    styleUrl: './fc-calc-section.component.scss',
})
export class FcCalcSectionComponent implements OnInit {
    form: FormGroup;
    @Output() filterProducts: EventEmitter<any> = new EventEmitter<any>();
    @Input() selectedLanguage: string = 'en';
    duration: any = {
        min: 1,
        max: 10,
    };
    interest: any = {
        min: 1,
        max: 30,
    };
    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({
            amount: [
                null,
                [
                    Validators.required,
                    Validators.min(1000),
                    Validators.max(20000000),
                ],
            ],
            interestRate: [this.interest.min],
            provider: [[]],
            durationUnit: ['years'],
            duration: [this.duration.min],
            financingType: [[]],
        });
    }

    ngOnInit(): void {
        this.form.valueChanges.subscribe((value) => {
            if (value.durationUnit === 'years') {
                this.duration = {
                    min: 1,
                    max: 10,
                };
            } else if (value.durationUnit === 'months') {
                this.duration = {
                    min: 1,
                    max: 11,
                };
            }
        });
    }

    onProviderChange(event: any) {
        const selected = this.form.value.provider as string[];
        const value = event.target.value;
        if (event.target.checked) {
            this.form.patchValue({ provider: [...selected, value] });
        } else {
            this.form.patchValue({
                provider: selected.filter((v) => v !== value),
            });
        }
    }

    onFinancingTypeChange(event: any) {
        const selected = this.form.value.financingType as string[];
        const value = event.target.value;
        if (event.target.checked) {
            this.form.patchValue({ financingType: [...selected, value] });
        } else {
            this.form.patchValue({
                financingType: selected.filter((v) => v !== value),
            });
        }
    }

    onSubmit() {
        this.filterProducts.emit(this.form.value);
    }

    onReset() {
        this.form.reset({
            amount: 0,
            interestRate: this.interest.min,
            provider: [],
            durationUnit: 'years',
            duration: this.duration.min,
            financingType: [],
        });

        this.getBackground('interest');
        this.getBackground('duration');
        this.getPosition('interest');
        this.getPosition('duration');
        this.filterProducts.emit(this.form.value);
    }

    getBackground(type: string): string {
        let percent = 0;
        let duration =
            this.form.value.duration == 1
                ? this.form.value.duration - 1
                : this.form.value.duration;
        let interest =
            this.form.value.interestRate == 1
                ? this.form.value.interestRate - 2
                : this.form.value.interestRate;
        if (type === 'interest') {
            percent = (interest / this.interest.max) * 100;
        } else if (type === 'duration') {
            percent = (duration / this.duration.max) * 100;
        }

        return `linear-gradient(to right, orange ${
            percent - 5
        }%, white ${percent}%)`;
    }

    getPosition(type: string): string {
        let percent = 0;
        let duration =
            this.form.value.duration == 1
                ? this.form.value.duration - 1
                : this.form.value.duration;
        let interest =
            this.form.value.interestRate == 1
                ? this.form.value.interestRate - 1
                : this.form.value.interestRate;
        if (type === 'interest') {
            percent = (interest / this.interest.max) * 100;
        } else if (type === 'duration') {
            percent = (duration / this.duration.max) * 100;
        }

        return `left: ${percent}%; transform: translateX(-${percent}%)`;
    }

    onAmountInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const raw = input.value.replace(/,/g, '').replace(/[^\d.]/g, '');
        this.form.get('amount')?.setValue(raw, { emitEvent: false });
        input.value = this.formatNumber(raw);
    }

    formatAmount(): void {
        const raw = this.form.get('amount')?.value;
        const formatted = this.formatNumber(raw);
        const inputEl = document.querySelector(
            'input[formControlName="amount"]'
        ) as HTMLInputElement;
        if (inputEl) inputEl.value = formatted;
    }

    formatNumber(value: string): string {
        if (!value) return '';
        const [intPart, decimalPart] = value.split('.');
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return decimalPart ? `${formattedInt}.${decimalPart}` : formattedInt;
    }
}
